import { DateTime, DateTimeFormatOptions, StringUnitLength } from 'luxon';

import { DateRange, DateRangeMandatory, DateRangeStartMandatory } from '../../api-types/common';
import {
  DateRangeDate,
  JSDateRange,
  JSDateRangeMandatory,
  JSDateRangeStartMandatory,
} from '../../types';
import { isNotNullDateTimeInvariant } from '../invariant';

export function getToday() {
  return DateTime.now().startOf('day').toJSDate();
}

export function getTomorrow() {
  return DateTime.now().startOf('day').plus({ days: 1 }).toJSDate();
}

export const getYesterday = () => {
  return getDateByNumberOfDaysPast(1);
};

export function getDateByNumberOfDaysPast(days: number) {
  return DateTime.now().startOf('day').minus({ days }).toJSDate();
}

function dateEquals(date1: DateRangeDate, date2: DateRangeDate): boolean {
  return date1 && date2 ? DateTime.fromJSDate(date1).equals(DateTime.fromJSDate(date2)) : false;
}

function rangeEquals(range1: JSDateRange, range2: JSDateRange): boolean {
  return dateEquals(range1.start, range2.start) && dateEquals(range1.end, range2.end);
}

export function getYesterdayRange() {
  const yesterday = getYesterday();
  return { start: yesterday, end: yesterday };
}

export function isYesterdayRange(range: JSDateRange) {
  return rangeEquals(range, getYesterdayRange());
}

export function getTodayRange() {
  const today = getToday();
  return { start: today, end: today };
}

export function isTodayRange(range: JSDateRange) {
  return rangeEquals(range, getTodayRange());
}

export function getNextNDaysRange(days: number): JSDateRange {
  return {
    start: getToday(),
    end: DateTime.now()
      .startOf('day')
      .plus({ days: days - 1 })
      .toJSDate(),
  };
}

export function isNextNDaysRange(days: number, range: JSDateRange) {
  return rangeEquals(range, getNextNDaysRange(days));
}

export function getLastNDaysRange(days: number): JSDateRange {
  return {
    start: DateTime.now()
      .startOf('day')
      .minus({ days: days - 1 })
      .toJSDate(),
    end: getToday(),
  };
}

export function getLastNDaysRangeWithTime(days: number): JSDateRange {
  const startDate = DateTime.now()
    .startOf('day')
    .minus({ days: days - 1 })
    .toJSDate();
  const endDate = getToday();
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  return {
    start: startDate,
    end: endDate,
  };
}

export function isLastNDaysRange(days: number, range: JSDateRange) {
  return rangeEquals(range, getLastNDaysRange(days));
}

export function isLastNDaysRangeWithTime(days: number, range: JSDateRange) {
  return rangeEquals(range, getLastNDaysRangeWithTime(days));
}

export function isEqualOpenDateRange(range1: JSDateRange, range2: JSDateRange) {
  return (
    dateEquals(range1.start, range2.start) &&
    (range1.end === range2.end || dateEquals(range1.end, range2.end))
  );
}

export function formatDateRange(
  locale: string,
  { start, end }: DateRange | JSDateRange,
  format: DateTimeFormatOptions = DateTime.DATE_SHORT,
  timezone?: string,
): string {
  const formattedStart = start ? formatDateTime(locale, start, format, timezone) : '';
  const formattedEnd = end ? formatDateTime(locale, end, format, timezone) : '';

  const sameDate = start != undefined && formattedStart === formattedEnd;

  return sameDate ? formattedStart : `${formattedStart} – ${formattedEnd}`;
}

export function formatDateTime(
  locale: string,
  dateTime: Date | string,
  format = DateTime.DATETIME_SHORT,
  timezone?: string,
): string {
  const date =
    dateTime instanceof Date ? DateTime.fromJSDate(dateTime) : DateTime.fromISO(dateTime);
  const zonedDate = timezone ? date.setZone(timezone) : date;
  return zonedDate.toLocaleString(format, { locale });
}

export function formatDate(
  locale: string,
  date: Date | string,
  format = DateTime.DATE_SHORT,
): string {
  return formatDateTime(locale, date, format);
}

export function isDateToday(date: string) {
  const isToday = DateTime.fromISO(date).startOf('day').equals(DateTime.now().startOf('day'));
  return isToday;
}

export function formatDateTimeRelative(
  locale: string,
  isoDateTime: string,
  style?: StringUnitLength | undefined,
  showTodayAsRelativeCalendar?: boolean, // e.g. Show "today" instead of "2 hours ago."
): string | null {
  const dateTime = DateTime.fromISO(isoDateTime);

  const isToday = dateTime.startOf('day').equals(DateTime.now().startOf('day'));

  if (isToday && !showTodayAsRelativeCalendar) {
    return dateTime.toRelative({ locale, style });
  }

  return dateTime.toRelativeCalendar({ locale, unit: 'days' });
}

export function isDateThisYear(date: Date | string) {
  const comparedDate = date instanceof Date ? DateTime.fromJSDate(date) : DateTime.fromISO(date);
  return DateTime.now().hasSame(comparedDate, 'year');
}

export function toJSDateRange(start?: string, end?: string): JSDateRange {
  const formattedStart = start ? DateTime.fromISO(start).toJSDate() : undefined;
  const formattedEnd = end ? DateTime.fromISO(end).toJSDate() : undefined;
  return {
    start: formattedStart,
    end: formattedEnd,
  };
}

export function toJSDateRangeMandatory(start: string, end: string): JSDateRangeMandatory {
  return {
    start: DateTime.fromISO(start).toJSDate(),
    end: DateTime.fromISO(end).toJSDate(),
  };
}

export function toJSDateRangeStartMandatory(
  start: string,
  end: string | null | undefined,
): JSDateRangeStartMandatory {
  return {
    start: DateTime.fromISO(start).toJSDate(),
    end: end ? DateTime.fromISO(end).toJSDate() : undefined,
  };
}

export function toDateRangeStartMandatory(start: Date, end?: Date | null): DateRangeStartMandatory {
  return {
    start: isNotNullDateTimeInvariant(DateTime.fromJSDate(start).toISODate()),
    end: end ? DateTime.fromJSDate(end).toISODate() : undefined,
  };
}

export function toDateRangeMandatory(start: Date, end: Date): DateRangeMandatory {
  return {
    start: isNotNullDateTimeInvariant(DateTime.fromJSDate(start).toISODate()),
    end: isNotNullDateTimeInvariant(DateTime.fromJSDate(end).toISODate()),
  };
}

export const toDayMonthHourMinute = (date: Date) => {
  const isDateCurrentYear = isDateThisYear(date);
  const timeToShow = DateTime.fromJSDate(date).toLocaleString(
    isDateCurrentYear
      ? { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  );
  return timeToShow;
};

export const toDayMonth = (date: Date) => {
  const isDateCurrentYear = isDateThisYear(date);
  const timeToShow = DateTime.fromJSDate(date).toLocaleString(
    isDateCurrentYear
      ? { day: 'numeric', month: 'short' }
      : { day: 'numeric', month: 'short', year: 'numeric' },
  );
  return timeToShow;
};

export function isoToDate(value: string | null | undefined) {
  return value != undefined ? DateTime.fromISO(value).toJSDate() : value;
}

export function dateToISO(value: Date | null | undefined) {
  // returns ISO 8601-compliant date in format of YYYY-MM-DD Example: 2024-08-23
  if (typeof value === 'undefined') {
    return undefined;
  }
  if (!value) {
    return null;
  }
  return DateTime.fromJSDate(value).toISODate();
}

export function isInThePast(date: Date | string) {
  if (date instanceof Date) return date < DateTime.now().toJSDate();

  const formattedDate = DateTime.fromISO(date).toJSDate();
  return formattedDate < DateTime.now().toJSDate();
}

export function isDateInFutureByMoreThanNDays(date: Date, day: number) {
  return (
    date >
    DateTime.now()
      .plus({ day: day + 1 })
      .endOf('day')
      .toJSDate()
  );
}

export function isValidDateString(dateString?: string) {
  return dateString && DateTime.fromISO(dateString).isValid;
}

export function toHourMinutesSecondsMilliseconds(date: string, setZone = true) {
  return DateTime.fromISO(date, { setZone }).toFormat('HH:mm:ss.SSS');
}

export function getTimeOptions(locale: string, minutesInterval: number) {
  const MINUTES_IN_24_HOURS = 1440;
  const multiplier = MINUTES_IN_24_HOURS / minutesInterval;

  const startTime = DateTime.utc().set({ hour: 0, minute: 0 }).setLocale(locale);

  return [...Array(multiplier)].map((_, i) => {
    const time = startTime.plus({ minutes: i * minutesInterval });

    return {
      value: time.toFormat('HH:mm'),
      label: time.toLocaleString(DateTime.TIME_SIMPLE),
    };
  });
}

export function formatDateTimeRaw(dateTime: string): string {
  // Handle date-only format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateTime)) {
    const [year, month, day] = dateTime.split('-');
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
      throw new Error('Invalid date');
    }

    return `${day}-${month}-${year}`;
  }

  // Handle date-time format with strict validation
  const match = dateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) {
    throw new Error('Invalid date');
  }

  const [, year, month, day, hour, minute] = match;

  // Validate date components
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);

  if (
    monthNum < 1 ||
    monthNum > 12 ||
    dayNum < 1 ||
    dayNum > 31 ||
    hourNum > 23 ||
    minuteNum > 59
  ) {
    throw new Error('Invalid date');
  }

  return `${day}-${month}-${year}, ${hour}:${minute}`;
}
